import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gameApi } from '../../../services/api/game.api';
import { getTeamColorClasses } from '../../../utils/teamColors';
import type { GameMode, RoundNumber, TeamName } from '@business-game/shared';

interface VerbatimModalProps {
  isOpen: boolean;
  verbatimHtml: string;
  currentRound: RoundNumber;
  teamId: number | null;
  sessionCode: string | null;
  teamName?: TeamName | null;
  gameMode?: GameMode | null;
  onNext: () => void;
}

export const VerbatimModal: React.FC<VerbatimModalProps> = ({
  isOpen,
  verbatimHtml,
  currentRound,
  teamId,
  sessionCode,
  teamName,
  gameMode,
  onNext,
}) => {
  const { t } = useTranslation();
  const chartRef = useRef<HTMLDivElement>(null);
  const [graphData, setGraphData] = useState<{
    business: number[];
    employees: number[];
    society: number[];
    environment: number[];
    labels: string[];
  } | null>(null);

  // Determine if teamlab (only 3 impacts) based on environment array
  const isTeamlab = graphData ? graphData.environment.length === 0 : false;
  const [showGraph, setShowGraph] = useState(false);
  const apexChartsRef = useRef<any>(null);
  const teamColors = getTeamColorClasses(teamName || null, gameMode ?? null);

  // Check if this round should show a graph (rounds 2 and 6)
  const shouldShowGraph = currentRound === 2 || currentRound === 6;

  // Get gradient classes based on team color
  const getGradientClasses = () => {
    if (teamColors.primary === 'bg-indigo-600') {
      return {
        bar: 'from-indigo-500 to-violet-500',
        text: 'from-indigo-600 to-violet-600',
      };
    } else if (teamColors.primary === 'bg-teal-600') {
      return {
        bar: 'from-teal-500 to-cyan-500',
        text: 'from-teal-600 to-cyan-600',
      };
    } else if (teamColors.primary === 'bg-emerald-600') {
      return {
        bar: 'from-emerald-500 to-teal-500',
        text: 'from-emerald-600 to-teal-600',
      };
    } else if (teamColors.primary === 'bg-blue-600') {
      return {
        bar: 'from-blue-500 to-cyan-500',
        text: 'from-blue-600 to-cyan-600',
      };
    } else {
      return {
        bar: 'from-orange-500 to-amber-500',
        text: 'from-orange-600 to-amber-600',
      };
    }
  };

  const gradients = getGradientClasses();

  // Get hover class for button
  const getButtonHoverClass = () => {
    if (teamColors.primary === 'bg-indigo-600') return 'hover:bg-indigo-700';
    if (teamColors.primary === 'bg-teal-600') return 'hover:bg-teal-700';
    if (teamColors.primary === 'bg-emerald-600') return 'hover:bg-emerald-700';
    if (teamColors.primary === 'bg-blue-600') return 'hover:bg-blue-700';
    return 'hover:bg-orange-700';
  };

  // Get watermark color
  const getWatermarkColor = () => {
    if (teamColors.primary === 'bg-indigo-600') return 'text-indigo-900';
    if (teamColors.primary === 'bg-teal-600') return 'text-teal-900';
    if (teamColors.primary === 'bg-emerald-600') return 'text-emerald-900';
    if (teamColors.primary === 'bg-blue-600') return 'text-blue-900';
    return 'text-orange-900';
  };

  // Get shadow color
  const getShadowColor = () => {
    if (teamColors.primary === 'bg-indigo-600') return 'shadow-indigo-500/20';
    if (teamColors.primary === 'bg-teal-600') return 'shadow-teal-500/20';
    if (teamColors.primary === 'bg-emerald-600') return 'shadow-emerald-500/20';
    if (teamColors.primary === 'bg-blue-600') return 'shadow-blue-500/20';
    return 'shadow-orange-500/20';
  };

  // Get icon color
  const getIconColor = () => {
    if (teamColors.primary === 'bg-indigo-600') return 'text-indigo-600';
    if (teamColors.primary === 'bg-teal-600') return 'text-teal-600';
    if (teamColors.primary === 'bg-emerald-600') return 'text-emerald-600';
    if (teamColors.primary === 'bg-blue-600') return 'text-blue-600';
    return 'text-orange-600';
  };

  // Get icon background color
  const getIconBgColor = () => {
    if (teamColors.primary === 'bg-indigo-600') return 'bg-indigo-50 ring-indigo-50/50';
    if (teamColors.primary === 'bg-teal-600') return 'bg-teal-50 ring-teal-50/50';
    if (teamColors.primary === 'bg-emerald-600') return 'bg-emerald-50 ring-emerald-50/50';
    if (teamColors.primary === 'bg-blue-600') return 'bg-blue-50 ring-blue-50/50';
    return 'bg-orange-50 ring-orange-50/50';
  };

  // Load graph data for rounds 2 and 6
  useEffect(() => {
    if (isOpen && shouldShowGraph && teamId) {
      const loadGraph = async () => {
        try {
          console.log('[VerbatimModal] Loading graph with:', {
            team: teamId,
            round: currentRound,
            sessionCode: sessionCode || 'NO SESSION CODE',
          });
          const data = await gameApi.generateGraph({
            team: teamId,
            round: currentRound,
            session: sessionCode || undefined,
          });
          setGraphData(data);
          setShowGraph(true);
        } catch (error) {
          console.error('Failed to load graph:', error);
          setShowGraph(false);
        }
      };
      loadGraph();
    } else {
      setShowGraph(false);
      setGraphData(null);
    }
  }, [isOpen, currentRound, teamId, sessionCode, shouldShowGraph]);

  // Render ApexCharts when graph data is available
  useEffect(() => {
    if (!showGraph || !graphData || !chartRef.current) return;

    // Dynamically import ApexCharts
    import('apexcharts').then((ApexChartsModule) => {
      const ApexCharts = ApexChartsModule.default;

      // Destroy previous chart if exists
      if (apexChartsRef.current) {
        apexChartsRef.current.destroy();
      }

      // Build series array - only include environment if not teamlab
      const series = [
        {
          name: t('game.impact_performance'),
          data: graphData.business,
        },
        {
          name: t('game.impact_cohesion'),
          data: graphData.employees,
        },
        {
          name: t('game.impact_wellbeing'),
          data: graphData.society,
        },
      ];

      // Only add Environment series if not teamlab
      if (!isTeamlab && graphData.environment.length > 0) {
        series.push({
          name: t('game.impact_environment'),
          data: graphData.environment,
        });
      }

      // Translate labels: 'Start' -> translated, 'Period X' -> translated
      const translatedLabels = graphData.labels.map((label) => {
        if (label === 'Start' || label === 'Début' || label === 'Inicio') {
          return t('game.graph_start');
        }
        // Match 'Period X' or 'Période X' or 'Período X'
        const periodMatch = label.match(/(?:Period|Période|Período)\s+(\d+)/i);
        if (periodMatch) {
          const periodNumber = periodMatch[1];
          return t('game.graph_period', { number: periodNumber });
        }
        return label;
      });

      const options = {
        series,
        chart: {
          type: 'area' as const,
          height: 320,
          fontFamily: 'Inter, sans-serif',
          toolbar: {
            show: false,
          },
          background: 'transparent',
          animation: {
            enabled: true,
            easing: 'easeinout',
            speed: 800,
          },
        },
        colors: isTeamlab
          ? ['#3b82f6', '#ec4899', '#eab308'] // Performance (blue), Cohésion (rose), Bien-être (yellow)
          : ['#3b82f6', '#ec4899', '#eab308', '#10b981'], // + Environnement (green) for leadlab
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: 'smooth',
          width: 3, // Increased width for better visibility
          colors: isTeamlab
            ? ['#3b82f6', '#ec4899', '#eab308']
            : ['#3b82f6', '#ec4899', '#eab308', '#10b981'],
        },
        markers: {
          size: 5,
          strokeWidth: 2,
          strokeColors: isTeamlab
            ? ['#3b82f6', '#ec4899', '#eab308']
            : ['#3b82f6', '#ec4899', '#eab308', '#10b981'],
          fillColors: ['#ffffff', '#ffffff', '#ffffff', '#ffffff'],
          hover: {
            size: 7,
          },
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.5, // Slightly more opaque for better visibility
            opacityTo: 0.1,
            stops: [0, 90, 100],
          },
        },
        xaxis: {
          categories: translatedLabels,
          labels: {
            style: {
              colors: '#64748b',
              fontSize: '11px',
              fontFamily: 'Inter, sans-serif',
            },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: {
              colors: '#64748b',
              fontSize: '11px',
              fontFamily: 'Inter, sans-serif',
            },
          },
        },
        grid: {
          borderColor: '#f1f5f9',
          strokeDashArray: 4,
          padding: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 10,
          },
        },
        tooltip: {
          theme: 'light',
          style: {
            fontSize: '12px',
            fontFamily: 'Inter, sans-serif',
          },
          shared: true, // Show all series values in tooltip
          intersect: false, // Show tooltip even when hovering between points
          y: {
            formatter: (val: number) => {
              return Math.round(val).toString();
            },
          },
        },
        legend: {
          position: 'bottom' as const,
          horizontalAlign: 'center' as const,
          fontFamily: 'Inter, sans-serif',
          offsetY: 8,
          markers: {
            radius: 12,
          },
        },
      };

      if (!chartRef.current) {
        return;
      }

      const chart = new ApexCharts(chartRef.current, options as any);
      chart.render();
      apexChartsRef.current = chart;
    });

    return () => {
      if (apexChartsRef.current) {
        apexChartsRef.current.destroy();
        apexChartsRef.current = null;
      }
    };
  }, [showGraph, graphData]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm transition-opacity"></div>

      {/* Modal Content */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className={`relative transform overflow-hidden rounded-3xl bg-white text-left shadow-2xl ${getShadowColor()} transition-all w-full max-w-4xl ring-1 ring-slate-200 animate__animated animate__zoomIn animate__faster`}>

            {/* Watermark */}
            <div className="absolute -bottom-24 -right-24 pointer-events-none select-none opacity-5 transform -rotate-12 z-0">
              <svg className={`w-[500px] h-[500px] ${getWatermarkColor()}`} fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>

            <div className="p-10 sm:p-14 flex flex-col items-start relative z-10 w-full">
              {/* Header */}
              <div className={`w-16 h-2 bg-gradient-to-r ${gradients.bar} rounded-full mb-8`}></div>
              <h3 className={`text-4xl sm:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r ${gradients.text} mb-8 leading-none tracking-tight`}>
                {t('game.results_title', 'Résultats')}
              </h3>

              {/* Dynamic Text Area */}
              <div className={`prose prose-lg text-slate-700 leading-relaxed mb-10 font-medium w-full max-w-none ${teamColors.primaryText}`}>
                <div
                  className="text-xl font-medium tracking-tight"
                  dangerouslySetInnerHTML={{
                    __html: (() => {
                      // Parse HTML and filter out empty paragraphs.
                      // Teamlab has 3 verbatims; leadlab has 4 (performance, social, sociétal, environnement).
                      const maxVerbatims = gameMode === 'leadlab' ? 4 : 3;
                      const parser = new DOMParser();
                      const doc = parser.parseFromString(verbatimHtml, 'text/html');
                      const paragraphs = Array.from(doc.querySelectorAll('p'))
                        .filter(p => p.textContent && p.textContent.trim().length > 0)
                        .slice(0, maxVerbatims)
                        .map(p => p.outerHTML);
                      return paragraphs.join('<br />');
                    })()
                  }}
                />
              </div>

              {/* Graph Zone (Rounds 2 & 6) */}
              {shouldShowGraph && (
                <>
                  {showGraph ? (
                    <div className="w-full mb-10">
                      <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 w-full">
                        <div ref={chartRef} className="w-full h-[320px]"></div>
                      </div>
                    </div>
                  ) : (
                    // Show icon placeholder when graph should be displayed but isn't loaded yet
                    <div className="w-full mb-10 flex justify-center">
                      <div className={`w-20 h-20 ${getIconBgColor()} rounded-full flex items-center justify-center shadow-sm ring-4`}>
                        <svg className={`w-10 h-10 ${getIconColor()}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Action Button */}
              <div className="w-full sm:w-auto">
                <button
                  type="button"
                  onClick={onNext}
                  className={`w-full sm:w-auto min-w-[240px] inline-flex justify-center items-center gap-3 rounded-2xl ${teamColors.primary} px-8 py-5 text-xl font-black text-white shadow-xl ${teamColors.shadow} ${getButtonHoverClass()} hover:scale-[1.02] active:scale-95 transition-all duration-200 ring-4 ${teamColors.primaryBorderLight}`}
                >
                  <span>{t('game.next_period', 'Période Suivante')}</span>
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
